import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight, CalendarCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  NATURE_OF_BUSINESS, INDUSTRY_SEGMENT, COMPANY_SIZE, PRODUCTS_INTEREST,
  PURPOSE_OF_VISIT, PREFERRED_FOLLOWUP, COMMUNICATION_CONSENT, CCTV_IMG, REGISTRATION_OPEN,
} from "@/data";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const initial = {
  company_name: "", visitor_name: "", designation: "", email: "", mobile: "", city: "",
  nature_of_business: [], nature_of_business_other: "",
  industry_segment: [], industry_segment_other: "",
  company_size: "",
  products_interest: [], products_interest_other: "",
  purpose_of_visit: [], preferred_followup: "",
  best_time_to_contact: "",
  communication_consent: "", data_consent: false,
};

function SectionHeader({ n, title }) {
  return (
    <div className="mb-8 flex items-center gap-4 border-b border-white/10 pb-4">
      <span className="font-mono-tech text-sm text-orange">{n}</span>
      <h3 className="font-display text-xl font-medium tracking-tight text-white">{title}</h3>
    </div>
  );
}

function TextField({ id, label, required, type = "text", value, onChange, placeholder, hint }) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id} className="font-sans text-sm text-steel">
        {label} {required && <span className="text-orange">*</span>}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`input-${id}`}
        className="h-12 rounded-sm border-white/15 bg-secondary text-white placeholder:text-muted-foreground/60 focus-visible:ring-orange focus-visible:ring-offset-0"
      />
      {hint && <span className="font-mono-tech text-[11px] text-muted-foreground">{hint}</span>}
    </div>
  );
}

function CheckboxGroup({ field, label, required, options, values, otherValue, onToggle, onOther }) {
  return (
    <fieldset className="flex flex-col gap-4">
      <legend className="mb-3 font-sans text-sm text-steel">
        {label} {required && <span className="text-orange">*</span>}
      </legend>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {options.map((opt) => {
          const checked = values.includes(opt);
          return (
            <label
              key={opt}
              className={`flex cursor-pointer items-center gap-3 rounded-sm border p-3 transition-colors duration-200 ${
                checked ? "border-orange bg-orange/10" : "border-white/10 bg-secondary hover:border-white/25"
              }`}
              data-testid={`${field}-${slug(opt)}`}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={() => onToggle(opt)}
                className="border-white/30 data-[state=checked]:border-orange data-[state=checked]:bg-orange"
              />
              <span className="font-sans text-sm text-white">{opt}</span>
            </label>
          );
        })}
      </div>
      {values.includes("Other") && (
        <Input
          value={otherValue}
          onChange={(e) => onOther(e.target.value)}
          placeholder="Please specify"
          data-testid={`${field}-other-input`}
          className="mt-1 h-11 rounded-sm border-white/15 bg-secondary text-white focus-visible:ring-orange focus-visible:ring-offset-0"
        />
      )}
    </fieldset>
  );
}

export default function RegistrationForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const doneRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, opt) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(opt) ? f[k].filter((x) => x !== opt) : [...f[k], opt],
    }));

  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const mobileValid = (m) => /^\+?\d[\d\s-]{9,17}$/.test(m.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.visitor_name.trim() || !form.city.trim()) {
      toast.error("Please fill all required visitor details.");
      return;
    }
    if (!emailValid(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!mobileValid(form.mobile)) {
      toast.error("Enter a valid mobile number (10–15 digits, include country code if outside India).");
      return;
    }
    if (form.nature_of_business.length === 0) {
      toast.error("Please select at least one nature of business.");
      return;
    }
    if (!form.communication_consent) {
      toast.error("Please choose your communication preference.");
      return;
    }
    if (!form.data_consent) {
      toast.error("Data consent is required to register.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/registrations", form);
      setDone(true);
      toast.success("Registration confirmed! Check your email.");
      setTimeout(() => doneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!REGISTRATION_OPEN) {
    return (
      <section id="register" className="relative overflow-hidden bg-ink py-24 md:py-32" data-testid="registration-closed">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,87,34,0.10),transparent_60%)]" />
        <div className="relative mx-auto max-w-2xl px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center"
          >
            <span className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-orange">
              <CalendarCheck className="h-4 w-4" /> Event Concluded
            </span>
            <h2 className="mt-8 font-display text-4xl font-semibold tracking-tighter text-white sm:text-5xl">
              Registration is now closed.
            </h2>
            <p className="mt-5 font-sans text-muted-foreground">
              Thank you to everyone who registered and visited <span className="text-white">KevalTek Expo</span>,
              Mysore&apos;s first CCTV &amp; surveillance showcase, on 30 &amp; 31 July 2026. Relive the highlights in the gallery above.
            </p>
            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row">
              <button
                onClick={() => document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 rounded-sm bg-orange px-7 py-4 font-mono-tech text-xs font-medium uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:-translate-y-1"
                data-testid="closed-view-gallery-btn"
              >
                View Highlights <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="tel:+919900456780"
                className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-7 py-4 font-mono-tech text-xs uppercase tracking-[0.15em] text-white transition-colors hover:border-orange hover:text-orange"
                data-testid="closed-contact-btn"
              >
                Contact: +91 99004 56780
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  if (done) {
    return (
      <section id="register" className="bg-ink py-24 md:py-32" data-testid="registration-success">
        <div ref={doneRef} className="mx-auto max-w-2xl px-6 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center"
          >
            <CheckCircle2 className="h-16 w-16 text-orange" />
            <h2 className="mt-8 font-display text-4xl font-semibold tracking-tighter text-white sm:text-5xl">
              You&apos;re registered.
            </h2>
            <p className="mt-4 font-sans text-muted-foreground">
              Thank you, <span className="text-white">{form.visitor_name}</span>. We&apos;ve sent a
              confirmation to <span className="text-white">{form.email}</span>. See you at KevalTek Expo,
              30 &amp; 31 July 2026.
            </p>
            <button
              onClick={() => { setForm(initial); setDone(false); }}
              className="mt-10 inline-flex items-center gap-2 rounded-sm border border-white/15 px-6 py-3 font-mono-tech text-xs uppercase tracking-[0.15em] text-white transition-colors hover:border-orange hover:text-orange"
              data-testid="register-another-btn"
            >
              Register another visitor
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="register" className="relative bg-ink" data-testid="registration-section">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Sticky left panel */}
        <div className="relative hidden lg:col-span-4 lg:block">
          <div className="sticky top-0 flex h-screen flex-col justify-between overflow-hidden p-12">
            <img src={CCTV_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-br from-ink/80 via-ink/70 to-ink" />
            <div className="relative">
              <span className="overline text-orange">Visitor Registration</span>
              <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tighter text-white xl:text-5xl">
                Let&apos;s stay in touch after the expo.
              </h2>
              <p className="mt-6 max-w-sm font-sans text-sm leading-relaxed text-muted-foreground">
                Fill in your details to help us stay in touch after the expo. Your information will be
                used for follow-ups related to KevalTek products and services.
              </p>
            </div>
            <div className="relative font-mono-tech text-xs text-muted-foreground">
              <div className="mb-2 text-orange">MYSORE · JULY 2026</div>
              4 sections · ~2 minutes
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-8">
          <form onSubmit={submit} className="mx-auto max-w-3xl px-6 py-20 md:px-12 lg:py-28" data-testid="registration-form">
            <div className="mb-16 lg:hidden">
              <span className="overline text-orange">Visitor Registration</span>
              <h2 className="mt-4 font-display text-4xl font-semibold tracking-tighter text-white">
                KevalTek Expo — Mysore
              </h2>
              <p className="mt-4 font-sans text-sm text-muted-foreground">
                Fill in your details to help us stay in touch after the expo.
              </p>
            </div>

            {/* 01 Visitor Details */}
            <div className="mb-16">
              <SectionHeader n="01" title="Visitor Details" />
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <TextField id="company_name" label="Company name" required value={form.company_name} onChange={(v) => set("company_name", v)} placeholder="Acme Systems Pvt Ltd" />
                <TextField id="visitor_name" label="Visitor name" required value={form.visitor_name} onChange={(v) => set("visitor_name", v)} placeholder="First and last name" />
                <TextField id="designation" label="Designation / Role" value={form.designation} onChange={(v) => set("designation", v)} placeholder="e.g. Purchase Manager" />
                <TextField id="email" label="Email" required type="email" value={form.email} onChange={(v) => set("email", v)} placeholder="you@company.com" />
                <TextField id="mobile" label="Mobile number" required value={form.mobile} onChange={(v) => set("mobile", v)} placeholder="9900456780" hint="Include country code if outside India" />
                <TextField id="city" label="City / Place" required value={form.city} onChange={(v) => set("city", v)} placeholder="Mysuru" />
              </div>
            </div>

            {/* 02 Business Information */}
            <div className="mb-16">
              <SectionHeader n="02" title="Business Information" />
              <div className="flex flex-col gap-10">
                <CheckboxGroup
                  field="nob" label="Nature of business" required options={NATURE_OF_BUSINESS}
                  values={form.nature_of_business} otherValue={form.nature_of_business_other}
                  onToggle={(o) => toggle("nature_of_business", o)} onOther={(v) => set("nature_of_business_other", v)}
                />
                <CheckboxGroup
                  field="industry" label="Industry segment" options={INDUSTRY_SEGMENT}
                  values={form.industry_segment} otherValue={form.industry_segment_other}
                  onToggle={(o) => toggle("industry_segment", o)} onOther={(v) => set("industry_segment_other", v)}
                />
                <fieldset>
                  <legend className="mb-3 font-sans text-sm text-steel">Company size</legend>
                  <RadioGroup value={form.company_size} onValueChange={(v) => set("company_size", v)} className="flex flex-wrap gap-3">
                    {COMPANY_SIZE.map((opt) => (
                      <label key={opt} className={`flex cursor-pointer items-center gap-2 rounded-sm border px-4 py-2.5 transition-colors ${form.company_size === opt ? "border-orange bg-orange/10" : "border-white/10 bg-secondary hover:border-white/25"}`} data-testid={`size-${slug(opt)}`}>
                        <RadioGroupItem value={opt} className="border-white/30 text-orange" />
                        <span className="font-mono-tech text-sm text-white">{opt}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </fieldset>
              </div>
            </div>

            {/* 03 Interest & Follow-up */}
            <div className="mb-16">
              <SectionHeader n="03" title="Interest & Follow-up" />
              <div className="flex flex-col gap-10">
                <CheckboxGroup
                  field="product" label="Products / solutions of interest" options={PRODUCTS_INTEREST}
                  values={form.products_interest} otherValue={form.products_interest_other}
                  onToggle={(o) => toggle("products_interest", o)} onOther={(v) => set("products_interest_other", v)}
                />
                <fieldset>
                  <legend className="mb-3 font-sans text-sm text-steel">Purpose of visit</legend>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {PURPOSE_OF_VISIT.map((opt) => {
                      const checked = form.purpose_of_visit.includes(opt);
                      return (
                        <label key={opt} className={`flex cursor-pointer items-center gap-3 rounded-sm border p-3 transition-colors ${checked ? "border-orange bg-orange/10" : "border-white/10 bg-secondary hover:border-white/25"}`} data-testid={`purpose-${slug(opt)}`}>
                          <Checkbox checked={checked} onCheckedChange={() => toggle("purpose_of_visit", opt)} className="border-white/30 data-[state=checked]:border-orange data-[state=checked]:bg-orange" />
                          <span className="font-sans text-sm text-white">{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="mb-3 font-sans text-sm text-steel">Preferred follow-up</legend>
                  <RadioGroup value={form.preferred_followup} onValueChange={(v) => set("preferred_followup", v)} className="flex flex-wrap gap-3">
                    {PREFERRED_FOLLOWUP.map((opt) => (
                      <label key={opt} className={`flex cursor-pointer items-center gap-2 rounded-sm border px-4 py-2.5 transition-colors ${form.preferred_followup === opt ? "border-orange bg-orange/10" : "border-white/10 bg-secondary hover:border-white/25"}`} data-testid={`followup-${slug(opt)}`}>
                        <RadioGroupItem value={opt} className="border-white/30 text-orange" />
                        <span className="font-sans text-sm text-white">{opt}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </fieldset>
                <TextField id="best_time_to_contact" label="Best time to contact" value={form.best_time_to_contact} onChange={(v) => set("best_time_to_contact", v)} placeholder="Weekdays, 2–5 PM" />
              </div>
            </div>

            {/* 04 Consents */}
            <div className="mb-12">
              <SectionHeader n="04" title="Consents" />
              <div className="flex flex-col gap-10">
                <fieldset>
                  <legend className="mb-3 font-sans text-sm text-steel">Communication consent <span className="text-orange">*</span></legend>
                  <RadioGroup value={form.communication_consent} onValueChange={(v) => set("communication_consent", v)} className="flex flex-col gap-3">
                    {COMMUNICATION_CONSENT.map((opt) => (
                      <label key={opt} className={`flex cursor-pointer items-start gap-3 rounded-sm border p-4 transition-colors ${form.communication_consent === opt ? "border-orange bg-orange/10" : "border-white/10 bg-secondary hover:border-white/25"}`} data-testid={`comm-consent-${slug(opt).slice(0, 20)}`}>
                        <RadioGroupItem value={opt} className="mt-0.5 border-white/30 text-orange" />
                        <span className="font-sans text-sm text-white">{opt}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </fieldset>

                <label className={`flex cursor-pointer items-start gap-3 rounded-sm border p-4 transition-colors ${form.data_consent ? "border-orange bg-orange/10" : "border-white/10 bg-secondary hover:border-white/25"}`} data-testid="data-consent">
                  <Checkbox checked={form.data_consent} onCheckedChange={(v) => set("data_consent", !!v)} className="mt-0.5 border-white/30 data-[state=checked]:border-orange data-[state=checked]:bg-orange" />
                  <span className="font-sans text-sm text-white">
                    I consent to KevalTek storing my details for expo follow-up and support purposes.
                    <span className="text-orange"> *</span>
                  </span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="group inline-flex w-full items-center justify-center gap-3 rounded-sm bg-orange px-8 py-5 font-mono-tech text-sm font-medium uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:-translate-y-1 hover:bg-orange-hover disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              data-testid="submit-registration-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
              ) : (
                <>Complete Registration <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
